/**
 * Produktfinder (Quiz) – sections/product-finder.liquid
 *
 * Alle Fragen, Antworten und Produktkarten sind serverseitig im DOM (übersetzt,
 * mit Marktpreisen). Dieses Script blendet sie als Quiz ein und aus:
 *
 *   Start → Frage → … → Ergebnis (Produktkarten) → Neu starten
 *
 * - Jede Antwort hat `data-pf-next` (Handle der nächsten Frage, leer = Ergebnis)
 *   und `data-pf-products` (Produkt-IDs). Produkte sammeln sich entlang des
 *   Pfads; die ersten N (data-pf-count) werden gezeigt, Ersatzprodukte
 *   (data-pf-fallback) füllen auf.
 * - Der Fortschrittsbalken rechnet mit dem längsten noch möglichen Restpfad,
 *   damit er in jedem Zweig bei 100 % endet.
 * - Zurück behält die bisherigen Antworten (Verlauf als Stack).
 *
 * Tracking (GTM dataLayer → GA4, Doku: docs/produktfinder.md):
 *   produktfinder_start, produktfinder_antwort, produktfinder_ergebnis,
 *   select_item, add_to_cart (jeweils mit item_list_name "Produktfinder").
 *   Es wird nur gepusht, wenn Cookiebot Statistik-Consent vorliegt.
 *
 * Kaufzuordnung: Beim Hinzufügen aus dem Finder werden die Warenkorb-Attribute
 * `_produktfinder = ja` und `_produktfinder_produkte = <ids>` gesetzt – sie
 * stehen an der Bestellung. Wird ein Finder-Produkt später auf der
 * Produktseite in den Warenkorb gelegt (Klick auf die Ergebniskarte),
 * greift dieselbe Zuordnung über sessionStorage.
 */
(function () {
  'use strict';

  const LIST_ID = 'produktfinder';
  const LIST_NAME = 'Produktfinder';
  const STORAGE_KEY = 'pf_products';
  const ATTR_FLAG = '_produktfinder';
  const ATTR_PRODUCTS = '_produktfinder_produkte';

  /* ---------------------------------------------------------------------- */
  /* Helfer                                                                  */
  /* ---------------------------------------------------------------------- */

  function consentGiven() {
    if (!window.Cookiebot) return true; // kein CMP (z. B. Theme-Vorschau) → GTM entscheidet
    return !!(window.Cookiebot.consent && window.Cookiebot.consent.statistics);
  }

  function track(event, params) {
    if (!consentGiven()) return;
    window.dataLayer = window.dataLayer || [];
    if (params && params.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(Object.assign({ event }, params || {}));
  }

  function readStoredIds() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function storeIds(ids) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      /* Storage nicht verfügbar – dann keine PDP-Zuordnung */
    }
  }

  function rememberIds(ids) {
    const merged = readStoredIds();
    ids.forEach(id => {
      if (!merged.includes(id)) merged.push(id);
    });
    storeIds(merged);
  }

  function parseIds(value) {
    return (value || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function itemFromCard(card, currency) {
    return {
      item_id: card.dataset.productId,
      item_name: card.dataset.productTitle,
      price: Number(card.dataset.price) || 0,
      currency,
      quantity: 1,
      item_list_id: LIST_ID,
      item_list_name: LIST_NAME,
    };
  }

  function setCartAttributes(productIds) {
    const attributes = {};
    attributes[ATTR_FLAG] = 'ja';
    attributes[ATTR_PRODUCTS] = productIds.join(',');
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch(() => {});
  }

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    }).then(response => {
      if (!response.ok) throw new Error('cart/add failed');
      return response.json();
    });
  }

  const ICON_CHECK =
    '<svg class="size-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
  const ICON_SPINNER =
    '<svg class="animate-spin size-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

  /* ---------------------------------------------------------------------- */
  /* Quiz                                                                    */
  /* ---------------------------------------------------------------------- */

  class ProductFinder {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.pfCurrency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      this.resultCount = Math.max(1, parseInt(root.dataset.pfCount, 10) || 3);
      this.fallbackIds = parseIds(root.dataset.pfFallback);
      this.startHandle = root.dataset.pfStart;

      this.screens = {
        start: root.querySelector('[data-pf-screen="start"]'),
        quiz: root.querySelector('[data-pf-screen="quiz"]'),
        result: root.querySelector('[data-pf-screen="result"]'),
      };
      this.questions = new Map();
      root.querySelectorAll('[data-pf-question]').forEach(el => {
        this.questions.set(el.dataset.pfQuestion, el);
      });
      this.templates = new Map();
      root.querySelectorAll('template[data-pf-product]').forEach(tpl => {
        this.templates.set(tpl.dataset.pfProduct, tpl);
      });

      this.progressEl = root.querySelector('[data-pf-progress]');
      this.progressBar = root.querySelector('[data-pf-progress-bar]');
      this.progressLabel = root.querySelector('[data-pf-progress-label]');
      this.resultsEl = root.querySelector('[data-pf-results]');
      this.emptyEl = root.querySelector('[data-pf-empty]');
      this.progressTemplate = (this.progressLabel && this.progressLabel.dataset.pfTemplate) || '';

      this.history = []; // [{ question, answer, products }]
      this.current = null;
      this.depthCache = new Map();

      this.bind();
    }

    bind() {
      const begin = this.root.querySelector('[data-pf-begin]');
      if (begin) begin.addEventListener('click', () => this.begin());

      const back = this.root.querySelector('[data-pf-back]');
      if (back) back.addEventListener('click', () => this.back());

      const restart = this.root.querySelector('[data-pf-restart]');
      if (restart) restart.addEventListener('click', () => this.restart());

      this.root.addEventListener('click', event => {
        const answer = event.target.closest('[data-pf-answer]');
        if (answer && this.root.contains(answer)) {
          this.choose(answer);
          return;
        }
        const atc = event.target.closest('[data-pf-atc]');
        if (atc) {
          this.addFromResult(atc);
          return;
        }
        const link = event.target.closest('[data-pf-link]');
        if (link) this.selectItem(link);
      });
    }

    /* --- Navigation ---------------------------------------------------- */

    begin() {
      if (!this.questions.has(this.startHandle)) return;
      this.history = [];
      this.screens.start.hidden = true;
      this.screens.quiz.hidden = false;
      this.showQuestion(this.startHandle);
      this.scrollTop();
      track('produktfinder_start', { produktfinder_seite: window.location.pathname });
    }

    restart() {
      this.history = [];
      this.hideAllQuestions();
      this.screens.result.hidden = true;
      this.screens.quiz.hidden = true;
      this.screens.start.hidden = false;
      this.scrollTop();
      const begin = this.root.querySelector('[data-pf-begin]');
      if (begin) begin.focus({ preventScroll: true });
    }

    back() {
      // Letzten Schritt vom Stack nehmen und dessen Frage mit der damals
      // gewählten Antwort markiert anzeigen (Antworten bleiben erhalten).
      const last = this.history.pop();
      if (!last) {
        this.restart();
        return;
      }
      this.screens.result.hidden = true;
      this.showQuestion(last.question, last.answer);
    }

    choose(button) {
      const section = button.closest('[data-pf-question]');
      if (!section) return;
      section.querySelectorAll('[data-pf-answer]').forEach(b => b.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');

      const step = {
        question: section.dataset.pfQuestion,
        questionLabel: section.dataset.pfLabel,
        answer: button.dataset.pfAnswer,
        answerLabel: button.dataset.pfLabel,
        products: parseIds(button.dataset.pfProducts),
      };
      this.history.push(step);

      track('produktfinder_antwort', {
        produktfinder_frage: step.question,
        produktfinder_frage_text: step.questionLabel,
        produktfinder_antwort: step.answer,
        produktfinder_antwort_text: step.answerLabel,
        produktfinder_schritt: this.history.length,
      });

      const next = button.dataset.pfNext;
      // kurze Pause, damit die Auswahl sichtbar ist
      window.setTimeout(() => {
        if (next && this.questions.has(next)) {
          this.showQuestion(next);
        } else {
          this.showResult();
        }
      }, 160);
    }

    hideAllQuestions() {
      this.questions.forEach(el => {
        el.hidden = true;
      });
    }

    showQuestion(handle, selectedAnswer) {
      const el = this.questions.get(handle);
      if (!el) return;
      this.hideAllQuestions();
      this.screens.result.hidden = true;
      el.querySelectorAll('[data-pf-answer]').forEach(b => {
        b.setAttribute('aria-pressed', selectedAnswer && selectedAnswer === b.dataset.pfAnswer ? 'true' : 'false');
      });
      el.hidden = false;
      this.current = handle;
      this.updateProgress(false);
      this.scrollTop();
      const title = el.querySelector('[data-pf-question-title]');
      if (title) title.focus({ preventScroll: true });
    }

    /* --- Fortschritt --------------------------------------------------- */

    /** Längster noch möglicher Restpfad ab Frage `handle` (inkl. dieser Frage). */
    remainingDepth(handle, visiting) {
      if (this.depthCache.has(handle)) return this.depthCache.get(handle);
      const el = this.questions.get(handle);
      if (!el) return 0;
      const seen = visiting || new Set();
      if (seen.has(handle)) return 1; // Zyklus-Schutz
      seen.add(handle);
      let deepest = 0;
      el.querySelectorAll('[data-pf-answer]').forEach(b => {
        const next = b.dataset.pfNext;
        if (next) deepest = Math.max(deepest, this.remainingDepth(next, seen));
      });
      seen.delete(handle);
      const depth = 1 + deepest;
      this.depthCache.set(handle, depth);
      return depth;
    }

    updateProgress(finished) {
      const done = this.history.length;
      const total = finished ? done : done + this.remainingDepth(this.current);
      const currentStep = finished ? done : done + 1;
      const percent = finished ? 100 : Math.round((done / Math.max(total, 1)) * 100);
      if (this.progressBar) this.progressBar.style.width = `${percent}%`;
      if (this.progressEl) this.progressEl.setAttribute('aria-valuenow', String(percent));
      if (this.progressLabel) {
        this.progressLabel.textContent = finished
          ? ''
          : this.progressTemplate.replace('__current__', currentStep).replace('__total__', total);
      }
    }

    /* --- Ergebnis ------------------------------------------------------ */

    collectProductIds() {
      const ids = [];
      this.history.forEach(step => {
        step.products.forEach(id => {
          if (!ids.includes(id) && this.templates.has(id)) ids.push(id);
        });
      });
      if (ids.length < this.resultCount) {
        this.fallbackIds.forEach(id => {
          if (ids.length < this.resultCount && !ids.includes(id) && this.templates.has(id)) ids.push(id);
        });
      }
      return ids.slice(0, this.resultCount);
    }

    showResult() {
      this.hideAllQuestions();
      const ids = this.collectProductIds();
      this.resultsEl.innerHTML = '';
      ids.forEach(id => {
        this.resultsEl.appendChild(this.templates.get(id).content.cloneNode(true));
      });
      this.emptyEl.hidden = ids.length > 0;
      this.screens.result.hidden = false;
      this.current = null;
      this.updateProgress(true);
      this.scrollTop();
      const title = this.screens.result.querySelector('[data-pf-result-title]');
      if (title) title.focus({ preventScroll: true });

      rememberIds(ids);
      const cards = Array.from(this.resultsEl.querySelectorAll('[data-pf-card]'));
      track('produktfinder_ergebnis', {
        produktfinder_pfad: this.history.map(h => h.answer).join(' > '),
        produktfinder_anzahl: ids.length,
        ecommerce: {
          item_list_id: LIST_ID,
          item_list_name: LIST_NAME,
          items: cards.map((card, index) => Object.assign(itemFromCard(card, this.currency), { index })),
        },
      });
    }

    selectItem(link) {
      const card = link.closest('[data-pf-card]');
      if (!card) return;
      rememberIds([card.dataset.productId]);
      track('select_item', {
        ecommerce: {
          item_list_id: LIST_ID,
          item_list_name: LIST_NAME,
          items: [itemFromCard(card, this.currency)],
        },
      });
    }

    addFromResult(button) {
      const card = button.closest('[data-pf-card]');
      if (!card || button.disabled) return;
      const original = button.innerHTML;
      button.disabled = true;
      button.innerHTML = ICON_SPINNER;

      const productId = card.dataset.productId;
      rememberIds([productId]);

      setCartAttributes(readStoredIds())
        .then(() => addToCart(card.dataset.variantId))
        .then(data => {
          button.innerHTML = ICON_CHECK;
          button.classList.add('bg-green-500');
          track('add_to_cart', {
            produktfinder_quelle: LIST_ID,
            ecommerce: {
              currency: this.currency,
              value: Number(card.dataset.price) || 0,
              items: [itemFromCard(card, this.currency)],
            },
          });
          // Slide-Cart öffnen + Zähler aktualisieren (wie add-to-cart.js)
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              const counter = document.querySelector('[data-cart-count]');
              if (counter) counter.textContent = cart.item_count;
            })
            .catch(() => {})
            .then(() => {
              document.dispatchEvent(new CustomEvent('cart:added', { detail: Object.assign({ __produktfinder: true }, data) }));
            });
        })
        .catch(() => {
          button.classList.add('bg-red-500');
        })
        .then(() => {
          window.setTimeout(() => {
            button.classList.remove('bg-green-500', 'bg-red-500');
            button.innerHTML = original;
            button.disabled = false;
          }, 2000);
        });
    }

    scrollTop() {
      const headerOffset = 60;
      const top = this.root.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      if (Math.abs(this.root.getBoundingClientRect().top - headerOffset) > 24) {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Zuordnung auf der Produktseite: Finder-Produkt später in den Warenkorb  */
  /* ---------------------------------------------------------------------- */

  function watchLaterAddToCart() {
    document.addEventListener('cart:added', event => {
      const data = event.detail || {};
      if (data.__produktfinder) return; // kam schon aus dem Finder selbst
      const productId = data.product_id ? String(data.product_id) : null;
      if (!productId) return;
      const stored = readStoredIds();
      if (!stored.includes(productId)) return;
      setCartAttributes(stored);
      track('add_to_cart', {
        produktfinder_quelle: LIST_ID,
        ecommerce: {
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
          value: (Number(data.price) || 0) / 100,
          items: [
            {
              item_id: productId,
              item_name: data.product_title || data.title || '',
              price: (Number(data.price) || 0) / 100,
              quantity: Number(data.quantity) || 1,
              item_list_id: LIST_ID,
              item_list_name: LIST_NAME,
            },
          ],
        },
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-product-finder]').forEach(root => {
      if (!root.__productFinder) root.__productFinder = new ProductFinder(root);
    });
  }

  watchLaterAddToCart();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme-Editor: Section neu geladen
  document.addEventListener('shopify:section:load', init);
})();
